import BaseModel from "../models/BaseModel";

import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";

import Terminal, { TerminalListBridgeInfo } from "../models/Terminal";
import Consumer from "../models/Consumer";
import EditableText from "../components/EditableText";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import TerminalStrip from "../models/TerminalStrip";
import { itx } from "../translationItx/itx";
import { getContrastTextColor } from "../modelViews/GraficElementUtils";
import { RDiv } from "../components/renderPrimitives/RDiv";
import { RenderTarget, useRenderTarget } from "../components/renderPrimitives/renderTarget";
import { RText } from "../components/renderPrimitives/RText";
import type TerminalBlock from "../models/TerminalBlock";
import FuseConsumerReference from "../models/FuseConsumerReference";
import BaseModelReference from "../models/BaseModelReference";

export default class TerminalTreeViewModel extends TreeViewModel {
  get typedObj(): Terminal {
    return this.obj as Terminal;
  }

  get isFolder(): boolean {
    return false;
  }

  getTagNeeded(): boolean {
    return false;
  }

  get iconUrl(): string | undefined {
    // if (this.typedObj) {
    //   return state.hostBaseUrl + "images/terminal/terminal.png";
    // }

    return undefined;
  }

  allowDrop(event: any): boolean {
    return true;
  }

  allowDrag(): boolean {
    return true;
  }

  navigate(ev: any, obj: BaseModel | undefined) {
    ev.stopPropagation();
    if (obj) {
      state.selectionManager.selectedObject = obj;
      state.selectionManager.focusSelectedObjectInTreeview();
    }
  }

  getFullTitle(isExpanded: boolean): string | undefined | JSX.Element {
    return <TerminalWireAndConsumer terminal={this.typedObj} />;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    if (obj.className == "Consumer") {
      const consumer = obj as Consumer;
      const terminal = this.typedObj;
      const connectedWithConsumer = consumer?.connectWithTerminal(terminal);
      if (connectedWithConsumer.connected) {
        (this.typedObj.parent as TerminalStrip)?.renumber(false);
        this.typedObj.raiseOnChange();
      }

      return new DropResult(connectedWithConsumer.connected, connectedWithConsumer.errortext);
    } else if (obj.className == "Terminal") {
      const consumer = (obj as Terminal).consumer;
      if (this.typedObj.consumer == undefined && consumer) {
        return this.handleDrop2(mode, consumer);
      }
    } else if (obj.className == "FuseConsumerReference") {
      const fuseConsumerReference = obj as FuseConsumerReference;
      const consumer = fuseConsumerReference.referencedObject;
      if (consumer?.className == "Consumer") {
        return this.handleDrop2(mode, consumer);
      }
    } else if (obj.className == "BaseModelReference") {
      const baseModelReference = obj as BaseModelReference;
      const consumer = baseModelReference.referencedObject;
      if (consumer?.className == "Consumer") {
        return this.handleDrop2(mode, consumer);
      }
    }

    return new DropResult(false);
  }

  handleInsert(event: any) {
    const contextMenuItems: any = {
      title: itx("terminalTreeViewModel.addTerminal", "Klemme einfügen"),
      items: [
        {
          id: `V`,
          label: itx("TerminalTreeviewModel.vorhereinfgen", "Vorher Einfügen"),
          onClick: () => {
            this.handleInsert1(false);
          },
        },
        {
          id: `N`,
          label: itx("TerminalTreeviewModel.nachhereinfgen", "Nachher Einfügen"),
          onClick: () => {
            this.handleInsert1(true);
          },
        },
      ],
    };
    state.contextMenuManager.show1(event, contextMenuItems, []);
  }

  handleInsert1(insertAfter: boolean) {
    const terminalStrip = this.typedObj.parent as TerminalStrip;
    if (terminalStrip) {
      const terminalBlock = this.typedObj.getTerminalBlock();
      if (this.typedObj.parent) {
        terminalBlock?.appendNewTerminalBlock(this.typedObj, this.typedObj.parent as TerminalStrip, insertAfter);
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // inline menu buttons
  //super.inlineButton_Properties(),
  ////////////////////////////////////////////////////////////////////////////////////////

  getButtons(): JSX.Element[] | undefined {
    const res = new Array<JSX.Element>();

    let ss = this.typedObj.tag.split(",");
    if (ss.length == 1) {
      ss = this.typedObj.tag.split("-");
    }
    if (ss.length > 1) {
      res.push(
        <TreeMenuIcon
          tooltipText="Klemme splitten"
          onClick={(ev: any) => {
            this.typedObj.splitIntoSingleTerminals();
          }}
          iconName="pause la-rotate-90" // no itx
          objName={this.obj.className + this.obj.fullTag}
          key={this.obj.className + this.obj.fullTag + "split"}
        />,
      );
    }

    if (this.typedObj.isMasterTerminal) {
      res.push(
        <TreeMenuIcon
          // tooltipKey={tooltipKey}
          tooltipText={itx("terminalTreeViewModel.addTerminal", "Klemme einfügen")}
          onClick={(event) => {
            this.handleInsert(event);
          }}
          iconName="plus-circle"
          objName={this.obj.className + this.obj.fullTag}
          key={this.obj.className + this.obj.fullTag + "add"} // no itx
        />,
      );
    }

    return res;
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // context menu
  //
  ////////////////////////////////////////////////////////////////////////////////////////

  ////////////////////////////////////////////////////////////////////////////////////////
  canBePastedInteractive(copiedObjClassName: string): boolean {
    return false;
  }
  ////////////////////////////////////////////////////////////
}

function selectCableType(ev: any, terminalBlock: TerminalBlock | undefined) {
  ev.stopPropagation();

  if (terminalBlock) {
    const items: any = [];

    for (const cableType of state.cableTypes.sortBy("name")) {
      items.push({
        id: cableType.id,
        label: cableType.name + (cableType.numberOfWires ? ` (${cableType.numberOfWires} Adern)` : ""),
        onClick: () => {
          if (terminalBlock) {
            terminalBlock.cableType = cableType;
          }
        },
      });
    }

    const contextMenuItems: any = {
      title: itx("TerminalTreeviewModel.kabeltypndern", "Kabeltyp ändern"),
      items: items,
    };
    state.contextMenuManager.show1(event, contextMenuItems, []);
  }
}

export function TerminalWireAndConsumer(props: { terminal: Terminal; showBridges?: boolean }) {
  const { terminal, showBridges } = props;

  const wireAndConsumer = terminal.getWireAndConsumer();

  const consumer = wireAndConsumer?.consumer;

  const channel = consumer?.getConnectedChannel();
  const fuse = consumer?.fuse;

  const cabinet = terminal.parent?.parent as BaseModel;
  const channelTag = cabinet && channel ? channel.getRelativeTag(cabinet) : channel?.tag;
  const heightInPx = 40; // because of buttons behind

  return (
    <RDiv
      style={{
        display: "flex",
        flexDirection: "row",
        height: heightInPx + "px",
        gap: "10px", // because of Headerstyling (header2)
      }}
    >
      <RDiv
        className="nav_C1 nav_navigate"
        style={{
          alignItems: "center",
          display: "flex",
        }}
        onClick={(ev) => (state.selectionManager.selectedObject = fuse)}
      >
        {fuse?.tag ? `${fuse?.tag}    ` : undefined}
      </RDiv>

      <RDiv
        className="nav_C2 nav_navigate"
        style={{
          alignItems: "center",
          display: "flex",
        }}
        onClick={(ev) => (state.selectionManager.selectedObject = channel)}
      >
        {" "}
        {channelTag}{" "}
      </RDiv>

      <RDiv
        className="nav_C3"
        style={{
          marginTop: terminal.isFirst ? "3px " : "0px",
          marginBottom: terminal.isLast ? "3px " : "0px",
          border: "1px solid grey",
          borderTop: terminal.isFirst ? "3px solid black" : "1px solid grey", // no itx
          borderBottom: terminal.isLast ? "3px solid black" : "1px solid grey", // no itx

          alignItems: "center",
          justifyContent: "stretch",
          display: "flex",

          paddingLeft: "5px",
          paddingRight: "5px",
        }}
      >
        <EditableText
          style={{
            fontWeight: "bold",
            marginLeft: "0px",
            marginRight: "0px",
            textAlign: "left",
          }}
          obj={terminal}
          property="tag"
          disableEdit={!terminal.selected}
          disableValidCheck={true}
          onBlur={() => terminal.parent?.raiseOnChange("childCollection")}
        />
      </RDiv>

      {showBridges && terminal.elcbInfos && (
        <QQ className="col_bridge_Q" infos={terminal.elcbInfos} backgroundColor="#b7dfff" /> // no itx
      )}

      {showBridges && terminal.fuseInfos && (
        <QQ className="col_bridge_F" infos={terminal.fuseInfos} backgroundColor="#9d7777ff" /> // no itx
      )}

      <TerminalWire terminal={terminal} consumer={consumer} />

      <TerminalConsumer terminal={terminal} consumer={consumer} heightInPx={heightInPx} />
    </RDiv>
  );
}

export function TerminalConsumer(props: {
  terminal: Terminal;
  consumer?: Consumer;
  heightInPx?: number;
  fontSizeInPx?: number;
}) {
  const target = useRenderTarget();

  const { terminal, consumer: consumerFrom, heightInPx = 32, fontSizeInPx = 0 } = props;
  const { paddingTopInPx, paddingBottomInPx, innerHeightInPx } = calcTerminalHeightsInPx(terminal, heightInPx);

  ///////////////////////////////////////////////////////////////////////////////
  // cabletype
  const terminalBlock = terminal.getTerminalBlock();
  const cableType = terminalBlock?.cableType;
  const wireCount = cableType?.numberOfWires ? cableType.numberOfWires + "x" : "?";
  const wireTypeTooltip = cableType ? `Kabeltyp: ${cableType.name}` : "Noch keine Kabeltype vergeben";

  //////////////////////////////////////////////////////////////////////////////////
  // consumer full Tag

  const mainConsumer = terminalBlock?.mainConsumer;
  let wireAndConsumer = terminal.getWireAndConsumer();
  const consumer = wireAndConsumer?.consumer;
  const consumerWireIndexWithoutPEandN = wireAndConsumer?.consumerWireIndexWithoutPEandN ?? -1;

  let textColor = "black";
  let isErrorWire = false;

  let arrow = " → "; // no itx
  if (target === RenderTarget.pdf) {
    arrow = " >> "; // no itx
  }

  let consumerFulltag = "";

  let wireIndex = -1;
  if (mainConsumer) {
    wireIndex = mainConsumer.fullWireIndex(consumerWireIndexWithoutPEandN);
  }

  const maxWireIndex = (cableType?.numberOfWires || 0) - 1;
  let wireFunction: string = "";

  if (!terminalBlock?.isCollapsed && consumerWireIndexWithoutPEandN >= 0) {
    wireFunction = consumer?.wireRequirement?.functionOfWires[consumerWireIndexWithoutPEandN] || "Reserve";
  }

  isErrorWire = wireIndex > maxWireIndex;

  if (consumer && mainConsumer) {
    if (consumer == mainConsumer) {
      // consumerFulltag = consumer.fullTag + ` (wireIndex=${wireIndexWithoutPEandN}) (F=${wireFunction})`;
      consumerFulltag = consumer.fullTag;
    } else {
      consumerFulltag = mainConsumer.fullTag + arrow + wireAndConsumer.consumer.getRelativeTag(mainConsumer);
    }
  } else {
    //
    // kein Consumer somit
    //    (nicht verkabelt)"
    //    oder nach dem Verteiler nicht mehr benutzt
    //

    let hasWire = true;
    if (mainConsumer) {
      let wireIndexFromTerminal = terminal.terminalIndex;
      const wireIndex2 = mainConsumer.fullWireIndex(wireIndexFromTerminal);
      if (wireIndex2 >= (cableType?.numberOfWires || 3)) {
        //
        // wenn der Index größer ist als die Anzahl der Adern im aufgelegten Kabel
        // dann setzen wir den Index auf -1 == > "nicht verkabelt"
        //
        hasWire = false;
      }
    }

    if (mainConsumer && hasWire && !isErrorWire) {
      consumerFulltag = mainConsumer.fullTag;
      wireFunction = "(Reserve)"; // no itx
    } else {
      consumerFulltag = "";
      wireFunction = itx("TerminalTreeviewModel.nichtverkabelt", "-- nicht verkabelt --");
    }

    textColor = "grey";
  }

  const elcbError = terminal.errors?.conflictingElcbs != undefined;

  return (
    <RDiv
      style={{
        paddingTop: paddingTopInPx + "px",
        paddingBottom: paddingBottomInPx + "px",
        height: heightInPx + "px",
      }}
    >
      <RDiv
        className="nav_C5"
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          height: innerHeightInPx + "px",
          paddingLeft: "10px",
          paddingRight: "10px",
          alignItems: "center",
          color: textColor,
          marginTop: terminal.isFirst ? "2px" : "0px",
          marginBottom: terminal.isLast ? "2px" : "0px",
          borderRight: "1px solid #ddd",
          borderTop: terminal.isFirst ? "1px solid #ddd" : undefined,
          borderBottom: terminal.isLast ? "1px solid #ddd" : undefined,
          backgroundColor: elcbError ? "#ff8f8fff" : "white",
        }}
      >
        <RDiv
          className="nav_navigate"
          style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          onClick={(ev) => (state.selectionManager.selectedObject = wireAndConsumer?.consumer)}
        >
          <RText style={{ whiteSpace: "nowrap", fontSize: fontSizeInPx ? fontSizeInPx + "px" : undefined }}>
            {consumerFulltag}
          </RText>

          {wireFunction && (
            <RText
              style={{
                paddingLeft: "10px",
                color: "#47d14eff",
                whiteSpace: "nowrap",
                fontSize: fontSizeInPx ? fontSizeInPx * 0.7 + "px" : "0.7em", // no itx
              }}
            >
              {`${wireFunction}`}
            </RText>
          )}
        </RDiv>

        {terminal.isFirst && terminal.consumer && (
          <RDiv
            className="nav_navigate"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "10px",
              width: "16px",
              height: "16px",
              borderRadius: "16px",
              border: "1px solid grey",
              fontSize: "8px",
            }}
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={wireTypeTooltip}
            data-tooltip-place="bottom"
            onClick={(ev) => selectCableType(ev, terminalBlock)}
          >
            <RText>{wireCount}</RText>
          </RDiv>
        )}
      </RDiv>
    </RDiv>
  );
}

export function calcTerminalHeightsInPx(
  terminal: Terminal,
  height: number = 32,
): {
  paddingTopInPx: number;
  paddingBottomInPx: number;
  innerHeightInPx: number;
} {
  const paddingTopInPx = terminal.isFirst ? 5 : 0;
  const paddingBottomInPx = terminal.isLast ? 5 : 0;
  const innerHeightInPx = height - paddingTopInPx - paddingBottomInPx;

  return {
    paddingTopInPx,
    paddingBottomInPx,
    innerHeightInPx,
  };
}

export function TerminalWire(props: { terminal: Terminal; consumer?: Consumer; heightInPx?: number }) {
  const { terminal, consumer: consumerFromOutside, heightInPx = 32 } = props;

  //  const consumer = consumerFromOutside || terminal.consumer;
  const wireAndConsumer = terminal.getWireAndConsumer();

  let consumer: Consumer | undefined = wireAndConsumer?.consumer;

  let textColor = "black";

  const terminalBlock = terminal.getTerminalBlock();
  if (!terminalBlock) {
    return null;
  }

  const mainConsumer = terminalBlock?.mainConsumer;

  const cableType = terminalBlock.cableType;
  const wireDefinitions = cableType?.wireDefinitions;

  let wireIndexFromTerminal = terminal.terminalIndex;
  let wireIndex = 2;
  if (mainConsumer) {
    wireIndex = mainConsumer.fullWireIndex(wireIndexFromTerminal);
  }

  let wireName = "";
  let wireHtmlColor = "transparent";

  if (wireIndex > 0 && wireDefinitions) {
    wireName = wireDefinitions[wireIndex]?.longName || "?Ader?";
    wireHtmlColor = cableType.htmlColors?.[wireIndex] || "#eee";
  }

  if (terminalBlock.isCollapsed) {
    wireHtmlColor = "#eee";
    wireName = "..."; // no itx
  } else if (terminalBlock.size == 1) {
    // wireName = "";
    // wireHtmlColor = "transparent";
  }

  textColor = getContrastTextColor(wireHtmlColor);

  const elcbError = terminal.errors?.conflictingElcbs;

  const { paddingTopInPx, paddingBottomInPx, innerHeightInPx } = calcTerminalHeightsInPx(terminal, heightInPx);

  const maxWireIndex = (terminalBlock?.cableType?.numberOfWires || 0) - 1;

  let isErrorWire = wireIndex > maxWireIndex;

  if (terminal.isMasterTerminal) {
    let consumerWiresWithoutPEandN = consumer?.wireRequirement?.numberOfLWiresWithoutPEandN || 0;
    let cableWiresWithoutPEandN = (cableType?.numberOfWires || 0) - 1;
    if (cableType?.hasGround) {
      cableWiresWithoutPEandN -= 1;
    }

    if (consumerWiresWithoutPEandN > cableWiresWithoutPEandN) {
      isErrorWire = true;
    }
  }

  let errorMessage: string | undefined = undefined;

  let borderColor = "transparent";
  if (wireHtmlColor == "#ffffff") {
    borderColor = "#ddd";
  }

  if (isErrorWire) {
    //
    // Eine Klemme mit einer fehlenden Ader aufgrund eines zu kleinen CableTypes
    // ist kein wirklicher Fehler
    // wir zeigen einfach keine Ader an
    //
    if (!consumer) {
      isErrorWire = false;
      wireName = "";
      borderColor = "transparent";
      wireHtmlColor = "transparent";
    } else {
      wireName = "error";
      textColor = "#f00";
      wireHtmlColor = "#fff";
      errorMessage = "Das Kabel hat zu wenig Adern";
    }
  }

  errorMessage ||= elcbError;

  return (
    <RDiv
      style={{
        display: "flex",
        flexDirection: "row",
        paddingLeft: "10px",
        height: heightInPx + "px",
        paddingTop: paddingTopInPx + "px",
        paddingBottom: paddingBottomInPx + "px",
      }}
    >
      <RDiv
        className="nav_C4"
        data-tooltip-id={isErrorWire || elcbError ? "tv-tooltip" : undefined}
        data-tooltip-html={errorMessage}
        data-tooltip-place={"bottom-end"} // no itx
        data-tooltip-variant="error" // no itx
        style={{
          alignItems: "center",
          textAlign: "center",
          justifyContent: "left",
          display: "flex",
          cursor: "default",

          paddingTop: "3px",
          paddingBottom: "3px",

          paddingLeft: "10px",
          paddingRight: "10px",

          marginTop: terminal.isFirst ? "2px" : "0px",
          marginBottom: terminal.isLast ? "2px" : "0px",

          borderLeft: "1px solid #ddd",
          borderTop: terminal.isFirst ? "1px solid #ddd" : undefined,
          borderBottom: terminal.isLast ? "1px solid #ddd" : undefined,
          borderTopLeftRadius: terminal.isFirst ? "10px" : undefined, // no itx
          borderBottomLeftRadius: terminal.isLast ? "10px" : undefined, // no itx

          height: innerHeightInPx + "px",

          backgroundColor: elcbError ? "#ff8f8fff" : "white",
        }}
      >
        <RDiv
          style={{
            backgroundColor: wireHtmlColor,
            width: "50px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderWidth: isErrorWire ? "2px" : "1px",
            borderColor: isErrorWire ? "red" : borderColor,
            borderStyle: "solid",
            borderRadius: "10px",
            height: heightInPx * 0.3 + 6 + "px",
            color: textColor,
          }}
        >
          {/* whiteSpace- force width calculation */}
          <RText style={{ whiteSpace: "nowrap", fontSize: heightInPx * 0.3 + "px" }}>{wireName || ""}</RText>
        </RDiv>
      </RDiv>
    </RDiv>
  );
}

export function QQ(props: { className?: string; infos: Array<TerminalListBridgeInfo>; backgroundColor: string }) {
  const { className: classNameFromOutside, infos, backgroundColor } = props;
  const res: any = [];

  const width = "0.7cm";

  infos.forEach((info) => {
    let className = classNameFromOutside || ""; // no itx
    if (info.dot && !print) {
      className += " withDot"; // no itx
    }
    res.push(
      <RDiv
        key={info.obj.id}
        className={className}
        style={{
          position: "relative",
          width: width,
          height: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
        }}
      >
        {info.dot && (
          <RDiv
            style={{
              position: "absolute",
              width: "8px",
              height: "8px",
              borderRadius: "4px",
              backgroundColor: "black",
            }}
          ></RDiv>
        )}

        {!info.bridgeIn && info.dot && (
          <RDiv style={{ position: "absolute", left: "0", top: "0px", width: width, textAlign: "center" }}>
            <RText style={{ fontSize: "14px" }}>{info.obj.tag}</RText>
          </RDiv>
        )}

        {info.bridgeIn && info.bridgeOut && (
          <RDiv style={{ height: "100%", width: "1px", backgroundColor: "black" }}></RDiv>
        )}
        {info.bridgeIn && !info.bridgeOut && (
          <RDiv
            style={{ position: "absolute", height: "50%", width: "1px", backgroundColor: "black", top: "0px" }}
          ></RDiv>
        )}
        {!info.bridgeIn && info.bridgeOut && (
          <RDiv
            style={{ position: "absolute", height: "50%", width: "1px", backgroundColor: "black", bottom: "0px" }}
          ></RDiv>
        )}
      </RDiv>,
    );
  });
  return (
    <RDiv
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "row",
        paddingLeft: "5px",
      }}
    >
      <RDiv
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: backgroundColor,
          paddingLeft: "10px",
          paddingRight: "10px",
        }}
      >
        {res}
      </RDiv>
    </RDiv>
  );
}
